import {
  DependencyError,
  drugApiPath,
  MedicationInfo,
  medicationLimits
} from '@cumu/shared';
import type AutoCompleteElement from '@github/auto-complete-element';
import { controller, target } from '@github/catalyst';
import { dispatchChange } from './util';

/*
keydown -> esc -> resets to medication name search, but only when value is empty
*/

@controller('medication-input')
class MedicationInputElement extends HTMLElement {
  @target declare displaySpan: HTMLInputElement;
  @target declare autocomplete: AutoCompleteElement; // https://github.com/github/auto-complete-element
  @target declare drugbank_pcid: HTMLInputElement; // varchar(16)
  @target declare drugbank_level: HTMLInputElement; // tinyint
  @target declare display_name: HTMLInputElement; // varchar(300)
  @target declare name: HTMLInputElement; // varchar(300)
  @target declare brand_name: HTMLInputElement; // varchar(150), nullable
  @target declare strength: HTMLInputElement; // varchar(50), nullable
  @target declare route: HTMLInputElement; // varchar(50), nullable
  @target declare form: HTMLInputElement; // varchar(100), nullable
  @target declare rxcui: HTMLInputElement; // int
  @target declare valueInput: HTMLInputElement;

  get organization_id() {
    return this.getAttribute('organization_id')!;
  }

  get person_id() {
    return this.getAttribute('person_id');
  }

  get activity_id() {
    return this.getAttribute('activity_id');
  }

  focusSearchInput() {
    this.autocomplete.inputElement?.focus();
  }

  searchInputFocused({ isTrusted }: FocusEvent) {
    this.#log('searchInputFocused', isTrusted);
  }

  searchInputBlurred() {
    this.#log('searchInputBlurred');
    // Close the dropdown on blur.
    // Timeout ensures this doesn't interfere with blurs that occur when selecting a suggestion.
    setTimeout(() => {
      if (this.autocomplete.open) {
        this.autocomplete.open = false;
      }
      this.autocomplete.inputElement!.value = '';
    }, 300); // do not reduce without testing quickly toggling between autocomplete suggestions with mouse.
  }

  async searchInputKeydown(event: KeyboardEvent) {
    const { key, shiftKey, metaKey, altKey, ctrlKey } = event;
    this.#log('searchInputKeydown', key);
    if (shiftKey || metaKey || altKey || ctrlKey) {
      return;
    }

    if (key === 'Backspace') {
      this.stepBack();
      return;
    }
  }

  searchInputChanged() {
    this.#log('searchInputChanged', this.autocomplete.inputElement?.value);
  }

  onAutocompleteLoadStart() {
    this.#log('onAutocompleteLoadStart', this.autocomplete.inputElement?.value);
  }

  onAutocompleteLoadEnd() {
    this.#log(
      'onAutocompleteLoadEnd',
      this.autocomplete.forElement?.childElementCount
    );
    this.autocomplete.forElement?.classList.remove('btn-busy');
    // Don't open the dropdown when the input is not focused.
    if (document.activeElement !== this.autocomplete.inputElement) {
      setTimeout(() => {
        if (this.autocomplete.open) {
          this.#log('CLOSE');
          this.autocomplete.open = false;
        }
      }, 0);
    }
  }

  onAutocompleteChange() {
    this.#log('onAutocompleteChange', this.autocomplete.inputElement?.value);
  }

  async onAutocompleteCommit({ target }: Event) {
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const data = JSON.parse(target.getAttribute('data-autocomplete-data')!);
    this.#log('onAutocompleteCommit');
    this.apply(data);
  }

  async stepBack() {
    if (this.autocomplete.inputElement?.disabled) {
      return;
    }
    const drugbank_pcid = this.drugbank_pcid.value;
    if (this.autocomplete.inputElement!.value.length === 0 && drugbank_pcid) {
      this.autocomplete.open = false;
      if (
        this.drugbank_level.value === '1' ||
        (this.brand_name.value && this.drugbank_level.value === '2')
      ) {
        this.apply(null);
        return;
      }
      try {
        this.displaySpan.parentElement?.classList.add('btn-busy');
        const response = await fetch(
          new URL(
            `/api/medications/product-concepts/${drugbank_pcid}/parents`,
            location.origin
          )
        );
        if (!response.ok) {
          throw new DependencyError(response, await response.text());
        }
        const parents = (await response.json()) as MedicationInfo[];
        // console.table(
        //   parents.map(({ brand_name, form, route, strength, name }) => ({
        //     brand_name,
        //     route,
        //     form,
        //     strength,
        //     name
        //   }))
        // );

        // console.table([
        //   {
        //     brand_name: this.brand_name.value,
        //     route: this.route.value,
        //     form: this.form.value,
        //     strength: this.strength.value,
        //     name: this.name.value
        //   }
        // ]);
        this.apply(
          parents.find(x => {
            // name, strength, route, form
            if ((x.brand_name ?? '') !== this.brand_name.value) {
              return false;
            }
            if (this.form.value) {
              return (
                (x.route ?? '') === this.route.value &&
                (x.strength ?? '') === this.strength.value
              );
            } else if (this.route.value) {
              return (
                (x.strength ?? '') === this.strength.value &&
                (x.form ?? '') === this.form.value
              );
            }
            return true;
          }) ?? null
        );
      } catch {
        this.apply(null);
      } finally {
        this.displaySpan.parentElement?.classList.remove('btn-busy');
      }
    }
  }

  apply(
    data:
      | (MedicationInfo & { dose_and_frequency?: string; prescriber?: string })
      | null
  ) {
    this.#log('apply', JSON.stringify(data, null, 2));

    // set selection.
    this.displaySpan.textContent =
      data?.name?.substr(0, medicationLimits.name) ?? '';
    this.drugbank_pcid.value = data?.drugbank_pcid ?? '';
    this.drugbank_level.value = data?.drugbank_level.toString() ?? '';
    this.name.value = data?.name?.substr(0, medicationLimits.name) ?? '';
    this.display_name.value =
      data?.display_name?.substr(0, medicationLimits.display_name) ?? '';
    this.brand_name.value =
      data?.brand_name?.substr(0, medicationLimits.brand_name) ?? '';
    this.strength.value =
      data?.strength?.substr(0, medicationLimits.strength) ?? '';
    this.route.value = data?.route?.substr(0, medicationLimits.route) ?? '';
    this.form.value = data?.form?.substr(0, medicationLimits.form) ?? '';
    this.rxcui.value = data?.rxcui?.toString() ?? '';

    if (data?.dose_and_frequency) {
      const input = this.closest('li')?.querySelector<HTMLInputElement>(
        'input[name$="/dose_and_frequency"]'
      );
      if (input) {
        input.value = data.dose_and_frequency;
        dispatchChange(input);
      }
    }
    if (data?.prescriber) {
      const input = this.closest('li')?.querySelector<HTMLInputElement>(
        'input[name$="/prescriber"]'
      );
      if (input) {
        input.value = data.prescriber;
        dispatchChange(input);
      }
    }

    dispatchChange(this.drugbank_pcid);

    // clear the search input.
    this.autocomplete.value = '';

    // clear the required input - so required validation will fire.
    this.valueInput.value = '';

    if (data?.drugbank_level === (data?.brand_name ? 5 : 4)) {
      // disable search
      this.autocomplete.src = '';
      this.autocomplete.open = false;
      // put a value in the required input so that the form will validate.
      this.valueInput.value = data.name;
      return;
    }

    // update the autocomplete source.
    this.autocomplete.src = drugApiPath({
      organization_id: this.organization_id,
      person_id: this.person_id,
      activity_id: this.activity_id,
      medication: data
    });

    // refocus the input element, to prevent onAutocompleteLoadEnd from closing the menu.
    // this can happen when the user clicks on a result instead of using the keyboard.
    this.autocomplete.inputElement?.focus();

    // start a new search
    this.autocomplete.inputElement?.dispatchEvent(new CustomEvent('focus'));

    // keep the menu open and set it busy.
    this.autocomplete.open = true;
    this.autocomplete.forElement?.classList.add('btn-busy');
  }

  #log(...args: any[]) {
    if (location.hostname === 'local.cumulus.care') {
      console.log('[medication-input]', ...args, this.autocomplete.src);
    }
  }
}

declare global {
  interface Window {
    MedicationInputElement: typeof MedicationInputElement;
  }
  interface HTMLElementTagNameMap {
    'medication-input': MedicationInputElement;
  }
}
